import React, {
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'
import { useSearchParams } from 'react-router-dom'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import PolicyVersionHistory from '@devnw/cyber-iam/abac/PolicyVersionHistory/PolicyVersionHistory'
// eslint-disable-next-line import/no-unresolved
import PolicySimulatorPanel from '@devnw/cyber-iam/abac/PolicySimulatorPanel/PolicySimulatorPanel'
import type {
  PolicyVersion as IamPolicyVersion,
  PolicySimulationQuery,
  PolicyDecision,
} from '@devnw/cyber-iam/iam/types'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import { useResource } from '../resource/useResource'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import type { Policy, PolicyTarget, PolicyCondition } from '../api/types'
import type {
  AdminCreatePolicyReq,
  AdminPolicyResp,
  AdminPolicyTargetJSON,
  AdminPolicyCondJSON,
} from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

/**
 * Policies — ABAC policy CRUD + version history + what-if simulator (OIDCPAM
 * Gate-6 Phase 6e, final governance page).
 *
 * A HYBRID migration:
 *  - The policy LIST + create/edit/delete → the reusable CrudTable/CrudForm +
 *    PamConfirmDialog (Phase 4a primitives), matching the Entitlements.tsx /
 *    Resources.tsx precedent.
 *  - The version-history dialog → `@devnw/cyber-iam/abac/PolicyVersionHistory`.
 *    The component owns the per-version rollback confirm (arm→confirm with
 *    amendment-M re-validation) — this page only supplies data and wires
 *    `onRollback` to the rollback mutation.
 *  - The simulate dialog → `@devnw/cyber-iam/abac/PolicySimulatorPanel`. The
 *    component owns the async what-if form, generation-token liveness guard,
 *    and the PERMIT/DENY render — this page only wires `onSimulate` to the
 *    non-mutating simulate call.
 *
 * Neither library component manages its own open/close state (both are
 * self-contained `<Card>`s, not modals), so — matching this page's existing
 * "Edit Policy" section pattern rather than inventing a new dialog overlay —
 * history/simulate render as inline SectionBlocks that appear when a policy
 * row's "History" action is clicked / the "Simulate" header button is
 * toggled, with an explicit Close control.
 *
 * WIRE MAPPING: adminListPolicies/adminListPolicyVersions declare their
 * `items` as untyped `Array<Record<string, unknown>>` (the schema is
 * `{"type":"object"}`), but every item has the same shape as the single-item
 * AdminPolicyResp returned by adminCreatePolicy/adminUpdatePolicy/
 * adminGetPolicy/adminDeletePolicy/adminRollbackPolicy, so items are cast to
 * that concrete type below rather than duplicating an inline structural type
 * (Entitlements.tsx / MtlsCAs.tsx precedent).
 *
 * LATENT BUGS FIXED (both are the same class the shared migration doc
 * flags — reading snake_case keys off a response the generated client's
 * decodeJSON()/fromWire() has ALREADY recursively camelCased, including
 * nested opaque `Record<string, unknown>` fields):
 *  1. `toPolicy`/`toPolicyTarget` (read side): the pre-migration `toPolicy`
 *     read `target.resource_types`/`target.resource_ids` off the decoded
 *     response. AdminPolicyTargetJSON confirms the real field names are
 *     `resourceTypes`/`resourceIds` — the snake_case reads always evaluated
 *     to `undefined`, silently rendering EMPTY resource-type/resource-id
 *     columns and edit-form fields in production for every policy.
 *  2. `formToCreateReq`/`formToUpdateReq` (write side, same root cause): the
 *     pre-migration code wrote `target: { resource_types, resource_ids }`
 *     into `AdminCreatePolicyReq.target` (a free-form `Record<string,
 *     unknown>` — the client's `toWire` leaves nested opaque records
 *     VERBATIM, it does not snake_case them), so those two fields were
 *     silently dropped on every create/update — the backend's target JSON
 *     tags are camelCase (same AdminPolicyTargetJSON contract). Fixed to
 *     write `resourceTypes`/`resourceIds`.
 *  3. `SimulateDialog`'s `DecisionResult` (read side): read
 *     `matched_policy_id`/`matched_version` off `AdminSimulatePolicyResp
 *     .decision` via an `as` cast to a hand-written snake_case type — that
 *     object is ALSO already camelCase post-fromWire, so those two fields
 *     were always blank (only `permit`, having no underscore, ever worked).
 *     Fixed by reading `matchedPolicyId`/`matchedVersion` and mapping into
 *     the component's `PolicyDecision` prop shape.
 */

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

const ValidationNote = styled.p`
  margin: 0;
  padding: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  border: 1px solid ${({ theme }) => asUnified(theme).colors?.warning ?? '#92400e'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => `${asUnified(theme).colors?.warning ?? '#92400e'}1a`};
  color: ${({ theme }) => asUnified(theme).colors?.warning ?? '#92400e'};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
`

// ---------------------------------------------------------------------------
// Wildcard validator (FORBID-A: no wildcard/standing privilege).
// Rejects "*", "all", "any" in any comma-separated action list.
// ---------------------------------------------------------------------------

const WILDCARD_TERMS = new Set(['*', 'all', 'any'])

function validateActions(raw: string): string | null {
  const items = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0)
  if (items.length === 0) return 'At least one action is required.'
  const bad = items.find(
    (item) => WILDCARD_TERMS.has(item) || item.includes('*')
  )
  if (bad !== undefined) {
    return `Wildcard/standing actions are not permitted: "${bad}"`
  }
  return null
}

function formatTimestamp(iso?: string): string | undefined {
  if (!iso) return undefined
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toLocaleString()
}

// ---------------------------------------------------------------------------
// Wire mapping: AdminPolicyResp / AdminPolicyTargetJSON (camelCase, real
// client response types) -> Policy / PolicyTarget (snake_case local shape
// consumed by CrudTable/CrudForm — see the LATENT BUGS FIXED note above).
// ---------------------------------------------------------------------------

function toPolicyTarget(raw: Record<string, unknown> | undefined): PolicyTarget {
  const t = (raw ?? {}) as AdminPolicyTargetJSON
  return {
    actions: t.actions ?? [],
    resource_types: t.resourceTypes ?? [],
    resource_ids: t.resourceIds ?? [],
    properties: t.properties ?? [],
  }
}

/** Same wire target, mapped to the CAMELCASE shape PolicyVersionHistory's
 *  `PolicyVersion.target` (from `@devnw/cyber-iam/iam/types`) expects —
 *  distinct from `toPolicyTarget` above, which maps to this page's local
 *  snake_case `PolicyTarget` (from `../api/types`) for CrudTable/CrudForm. */
function toIamTarget(raw: Record<string, unknown> | undefined): {
  actions: string[]
  resourceTypes: string[]
  resourceIds: string[]
  properties: string[]
} {
  const t = (raw ?? {}) as AdminPolicyTargetJSON
  return {
    actions: t.actions ?? [],
    resourceTypes: t.resourceTypes ?? [],
    resourceIds: t.resourceIds ?? [],
    properties: t.properties ?? [],
  }
}

function toPolicyConditions(
  raw: Array<Record<string, unknown>> | undefined
): PolicyCondition[] {
  return (raw ?? []).map((c) => {
    const cond = c as unknown as AdminPolicyCondJSON
    return {
      field: cond.field ?? '',
      operator: cond.operator ?? '',
      value: cond.value ?? '',
    }
  })
}

function toPolicy(raw: Record<string, unknown>): Policy {
  const r = raw as AdminPolicyResp
  return {
    id: r.id ?? '',
    tenant_id: r.tenantId ?? '',
    name: r.name ?? '',
    description: r.description ?? '',
    enabled: r.enabled ?? false,
    priority: r.priority ?? 0,
    effect: r.effect === 'deny' ? 'deny' : 'permit',
    version: r.version ?? 0,
    created_at: r.createdAt ?? '',
    updated_at: r.updatedAt ?? '',
    target: toPolicyTarget(r.target),
    conditions: toPolicyConditions(r.conditions),
  }
}

/** Map an adminListPolicyVersions item (camelCase, real wire shape) -> the
 *  PolicyVersionHistory component's `PolicyVersion` prop type. `createdAt` is
 *  pre-formatted here — the component never parses dates. */
function toIamPolicyVersion(
  raw: Record<string, unknown>,
  fallbackPolicyId: string
): IamPolicyVersion {
  const r = raw as {
    policyId?: string
    version?: number
    effect?: string
    target?: Record<string, unknown>
    createdAt?: string
  }
  return {
    policyId: r.policyId ?? fallbackPolicyId,
    version: r.version ?? 0,
    effect: r.effect === 'deny' ? 'deny' : 'permit',
    target: toIamTarget(r.target),
    createdAt: formatTimestamp(r.createdAt),
  }
}

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

const columns: CrudColumn<Policy>[] = [
  { key: 'name', header: 'Name' },
  { key: 'effect', header: 'Effect' },
  { key: 'priority', header: 'Priority' },
  { key: 'version', header: 'Version' },
  {
    key: 'enabled',
    header: 'Enabled',
    render: (p) => (p.enabled ? 'Yes' : 'No'),
  },
]

// ---------------------------------------------------------------------------
// Form fields for create / edit
// ---------------------------------------------------------------------------

const fields: CrudField[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'description', label: 'Description', type: 'text' },
  {
    name: 'effect',
    label: 'Effect (permit | deny)',
    type: 'text',
    required: true,
  },
  {
    name: 'priority',
    label: 'Priority (lower = higher precedence)',
    type: 'text',
  },
  { name: 'enabled', label: 'Enabled', type: 'checkbox' },
  {
    name: 'actions',
    label: 'Actions (comma-separated, no wildcards)',
    type: 'text',
    required: true,
  },
  {
    name: 'resource_types',
    label: 'Resource Types (comma-separated, optional)',
    type: 'text',
  },
  {
    name: 'resource_ids',
    label: 'Resource IDs (comma-separated, optional)',
    type: 'text',
  },
]

const CREATE_INITIAL: Record<string, unknown> = {
  effect: 'permit',
  priority: '10',
  enabled: true,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const splitCSV = (v: unknown): string[] =>
  String(v ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

function formToCreateReq(
  tenantId: string,
  v: Record<string, unknown>
): AdminCreatePolicyReq {
  return {
    tenantId,
    name: String(v.name ?? ''),
    description: String(v.description ?? ''),
    effect: String(v.effect ?? 'permit') === 'deny' ? 'deny' : 'permit',
    priority: Number(v.priority) || 0,
    enabled: Boolean(v.enabled),
    // Field names match AdminPolicyTargetJSON (actions/resourceTypes/
    // resourceIds/properties) — see the LATENT BUGS FIXED note (#2) above:
    // AdminCreatePolicyReq.target is a free-form Record<string, unknown>, so
    // a snake_case key here would silently not match what the backend
    // decodes.
    target: {
      actions: splitCSV(v.actions),
      resourceTypes: splitCSV(v.resource_types),
      resourceIds: splitCSV(v.resource_ids),
      properties: [],
    },
    conditions: [],
  }
}

function formToUpdateReq(
  tenantId: string,
  policyId: string,
  v: Record<string, unknown>
): AdminCreatePolicyReq {
  return {
    ...formToCreateReq(tenantId, v),
    policyId,
  }
}

// ---------------------------------------------------------------------------
// Policies page
// ---------------------------------------------------------------------------

export const Policies = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const [searchParams] = useSearchParams()
  const client = useAdminGenClient()

  const [editing, setEditing] = useState<Policy | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionValidation, setActionValidation] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Policy | null>(null)

  const [historyTarget, setHistoryTarget] = useState<Policy | null>(null)
  const [historyVersions, setHistoryVersions] = useState<IamPolicyVersion[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | undefined>(undefined)

  const [showSimulate, setShowSimulate] = useState(false)

  const rs = useResource<Policy>({
    list: async () => {
      const resp = await client.adminListPolicies({ tenantId: tenant })
      return (resp.items ?? []).map(toPolicy)
    },
    remove: async (id) => {
      await client.adminDeletePolicy({ tenantId: tenant, policyId: id })
    },
  })

  const loadHistory = useCallback(
    (policyId: string): void => {
      setHistoryLoading(true)
      setHistoryError(undefined)
      client
        .adminListPolicyVersions({ tenantId: tenant, policyId })
        .then((resp) => {
          setHistoryVersions(
            (resp.items ?? []).map((raw) => toIamPolicyVersion(raw, policyId))
          )
        })
        .catch((e: unknown) => {
          setHistoryError(
            e instanceof Error ? e.message : 'Failed to load versions'
          )
        })
        .finally(() => setHistoryLoading(false))
    },
    [client, tenant]
  )

  // Load (or clear) the version-history panel's data whenever a NEW policy's
  // history is opened/closed. Rollback-triggered reloads go through
  // `handleHistoryRefresh` (wired to PolicyVersionHistory's `onRefresh`)
  // rather than re-running this effect.
  useEffect(() => {
    if (!historyTarget) {
      setHistoryVersions([])
      setHistoryError(undefined)
      return
    }
    loadHistory(historyTarget.id)
  }, [historyTarget, loadHistory])

  // The "current version" shown in the history panel must track the main
  // list's live data (a rollback republishes a new current version), not the
  // possibly-stale `historyTarget` reference captured when the panel opened.
  const historyPolicy = historyTarget
    ? rs.items.find((p) => p.id === historyTarget.id) ?? historyTarget
    : null

  const handleRollback = useCallback(
    async (toVersion: number): Promise<void> => {
      if (!historyTarget) return
      await client.adminRollbackPolicy({
        tenantId: tenant,
        policyId: historyTarget.id,
        toVersion,
      })
    },
    [client, tenant, historyTarget]
  )

  const handleRolledBack = useCallback((): void => {
    rs.reload()
      .then(() => {})
      .catch(() => {})
  }, [rs])

  const handleHistoryRefresh = useCallback((): void => {
    if (historyTarget) loadHistory(historyTarget.id)
  }, [historyTarget, loadHistory])

  const handleSimulate = useCallback(
    async (query: PolicySimulationQuery): Promise<PolicyDecision> => {
      const resp = await client.adminSimulatePolicy({
        tenantId: tenant,
        query: {
          subjectId: query.subjectId,
          subjectType: query.subjectType,
          resourceId: query.resourceId,
          resourceType: query.resourceType,
          action: query.action,
          property: query.property,
        },
        policies: [],
      })
      // See LATENT BUGS FIXED (#3) above: `resp.decision` is opaque but
      // already camelCase post-fromWire.
      const decision = (resp.decision ?? {}) as {
        permit?: boolean
        reason?: string
        matchedPolicyId?: string
        matchedVersion?: number
      }
      return {
        permit: Boolean(decision.permit),
        reason: decision.reason,
        matchedPolicyId: decision.matchedPolicyId,
        matchedVersion: decision.matchedVersion,
        matchedPolicies: resp.matchedPolicies,
      }
    },
    [client, tenant]
  )

  const validateAndCreate = async (
    v: Record<string, unknown>
  ): Promise<void> => {
    const actErr = validateActions(String(v.actions ?? ''))
    if (actErr) {
      setActionValidation(actErr)
      return
    }
    setActionValidation(null)
    try {
      await client.adminCreatePolicy(formToCreateReq(tenant, v))
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const validateAndEdit = async (v: Record<string, unknown>): Promise<void> => {
    if (!editing) return
    const actErr = validateActions(String(v.actions ?? ''))
    if (actErr) {
      setActionValidation(actErr)
      return
    }
    setActionValidation(null)
    try {
      await client.adminUpdatePolicy(formToUpdateReq(tenant, editing.id, v))
      await rs.reload()
      setEditing(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (p: Policy): ReactNode => (
    <CrudActionsRow>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setEditing(p)
          setActionError(null)
          setActionValidation(null)
        }}
      >
        Edit
      </CrudSecondaryButton>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setHistoryTarget(p)
          setActionError(null)
        }}
      >
        History
      </CrudSecondaryButton>
      <CrudDangerButton
        type="button"
        onClick={() => {
          setActionError(null)
          setPendingDelete(p)
        }}
      >
        Delete
      </CrudDangerButton>
    </CrudActionsRow>
  )

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>
          Please select a tenant to manage policies.
        </TenantGateText>
      </PageStack>
    )
  }

  const deepLinkResourceIds = searchParams.get('resource_ids')
  const deepLinkActions = searchParams.get('actions')
  const createInitial: Record<string, unknown> = {
    ...CREATE_INITIAL,
    ...(deepLinkResourceIds ? { resource_ids: deepLinkResourceIds } : {}),
    ...(deepLinkActions ? { actions: deepLinkActions } : {}),
  }

  return (
    <PageStack>
      <HeaderRow>
        <PageTitle>Policies</PageTitle>
        <CrudSecondaryButton
          type="button"
          onClick={() => setShowSimulate((v) => !v)}
        >
          {showSimulate ? 'Hide simulator' : 'Simulate'}
        </CrudSecondaryButton>
      </HeaderRow>

      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}

      <SectionBlock>
        <SectionTitle>All Policies</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Policies list"
          emptyMessage="No policies configured yet."
        />
      </SectionBlock>

      {editing && (
        <SectionBlock>
          <SectionTitle>Edit Policy (publishes new version)</SectionTitle>
          {actionValidation && (
            <ValidationNote role="alert">{actionValidation}</ValidationNote>
          )}
          <CrudForm
            key={editing.id}
            fields={fields}
            initial={{
              name: editing.name,
              description: editing.description,
              effect: editing.effect,
              priority: String(editing.priority),
              enabled: editing.enabled,
              actions: editing.target.actions.join(', '),
              resource_types: editing.target.resource_types.join(', '),
              resource_ids: editing.target.resource_ids.join(', '),
            }}
            onSubmit={validateAndEdit}
            submitLabel="Save (new version)"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => {
              setEditing(null)
              setActionError(null)
              setActionValidation(null)
            }}
          >
            Cancel
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      <SectionBlock>
        <SectionTitle>Create Policy</SectionTitle>
        {actionValidation && !editing && (
          <ValidationNote role="alert">{actionValidation}</ValidationNote>
        )}
        <CrudForm
          fields={fields}
          initial={createInitial}
          onSubmit={validateAndCreate}
          submitLabel="Create Policy"
        />
      </SectionBlock>

      {historyPolicy && (
        <SectionBlock>
          <PolicyVersionHistory
            versions={historyVersions}
            currentVersion={historyPolicy.version}
            onRollback={handleRollback}
            onRolledBack={handleRolledBack}
            onRefresh={handleHistoryRefresh}
            loading={historyLoading}
            error={historyError}
            title={`Version history — ${historyPolicy.name}`}
            testID="policies-history"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => setHistoryTarget(null)}
          >
            Close
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      {showSimulate && (
        <SectionBlock>
          <PolicySimulatorPanel
            onSimulate={handleSimulate}
            testID="policies-simulate"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => setShowSimulate(false)}
          >
            Close
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete policy"
        body={`Delete policy "${pendingDelete?.name ?? pendingDelete?.id ?? ''}"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
